'use client';

/**
 * SocialProofSection — R4-P3 light big-stats rebuild (masterplan §2.3,
 * as amended by [AMENDED per A6] + [AMENDED per B1/D3] + [AMENDED per C5]).
 *
 * This file is now the SECOND transparent `<section>` child rendered inside
 * `LightInterlude` (the sole `LightBand`/paper owner, [AMENDED per B1/D3]) —
 * it does NOT wrap itself in a paper background. It replaces the round-3
 * dark filled-mock presentation (Clinica showcase card + tenure counter +
 * KPI tiles + illustrative trend chart, all on `#0B0C0E`) with the light
 * editorial big-stats grammar (attio `t1N69kp03G`): eyebrow, two-tone
 * headline, exactly 3 real `BigStat` tiles, and one `BigStatCurve` ink
 * sweep. The Clinica testimonial/attribution MIGRATED to
 * `SerifQuoteSection.tsx` (the light interlude's first section, §2.6) — it
 * is intentionally NOT re-rendered here (§1.1 "de-duplicated").
 *
 * The server snapshot pipeline (`computeSocialProofSnapshot`,
 * `isPlausibleAggregate`, the privacy/sanity fail-closed gate) and the
 * `snapshot` prop-threading contract (`page.tsx -> LandingPage ->
 * SocialProofSection`) are UNTOUCHED (Law #7) — this file only changed HOW
 * the same `SocialProofSnapshot` renders.
 *
 * [AMENDED per A6]: exactly 3 `BigStat` items — `leadsProcessed`,
 * `clientsWon`, `conversionRate` — the only three real numbers the snapshot
 * produces. NO fourth illustrative KPI (the original §2.3 draft's "ore
 * economisite / lună" is DELETED — no data source, and Law #8 bans
 * fabricated KPIs). `snapshot === null` keeps the EXISTING fail-closed
 * presentation EXACTLY: an em dash for all three (`UnavailableStat` below —
 * `BigStat` has no built-in "unavailable" variant, so this is a small local
 * static twin matching its visual grammar, not a fork of the frozen
 * primitive), the `light.proof.unavailable` line, no ticker (an em dash has
 * nothing to tick up to — `BigStat`'s `NumberTicker` is never mounted on
 * this branch).
 *
 * [AMENDED per C5]: `formatMetricCount`/`formatMetricRate` moved to
 * `social-proof/formatters.ts` (pure); `SocialProofMotionController.tsx` is
 * DELETED along with every `data-kpi-*`/`data-chart-*` selector it used —
 * all new number/curve motion lives in `social-proof/BigStatsMotion.tsx`.
 *
 * `BigStatCurve` itself renders unconditionally (real or unavailable path)
 * — it is an explicitly-labeled `IllustrativeLabel` decorative sweep,
 * disconnected from the real KPI values in BOTH states (masterplan §2.3/B2
 * never gate its static render on snapshot presence).
 *
 * R5-P4 fix (SERIOUS, founder-reported + lead-verified live gap): C5's
 * ORIGINAL "only the entrance MOTION is gated [on snapshot]" clause is
 * SUPERSEDED — that gate left the band's reveal (row fade/rise) and the
 * curve's clip-path sweep COMPLETELY static (opacity:1, clip-path:none from
 * first paint, permanently) on the `snapshot === null` fallback branch,
 * which is what STAGING renders today; a founder read this as "the
 * animation doesn't work." `BigStatsMotion` now mounts UNCONDITIONALLY, and
 * the row/curve's hidden-baseline CSS classes (+ their no-JS override) are
 * UNCONDITIONAL too — `BigStatsMotion` never reads `snapshot` at all, it
 * only queries the two `data-big-stats-*` hooks, which exist on both
 * branches, so the reveal + sweep now play identically regardless of which
 * branch rendered. ONLY the digit count-up stays scoped to `snapshot`
 * truthiness, via the untouched `{snapshot ? <BigStat/> : <UnavailableStat/>}`
 * ternary below — an em dash still never ticks.
 *
 * R7 paper flip (2026-07-17, lane L3, design contract L3.1 "Growth/
 * SocialProof band -> paper: All text to PAPER_INK.*"): every ink class
 * THIS file owns directly (the local `UnavailableStat` twin above, the
 * headline spans, the unavailable-state paragraph, the real curve caption)
 * converted from `ui-light/light-tokens.ts`'s `LIGHT_TOKENS` to
 * `ui/paper-tokens.ts`'s `PAPER_INK`. This file was already "light" before
 * R7 (it renders transparently inside `LightInterlude`'s `LightBand`, per
 * this docblock's own opening paragraph) — but `LightBand`/`LightEyebrow`/
 * `BigStat`/`BigStatCurve` (the actual page-bg + eyebrow-chip + real
 * digit/curve renderers) were shared `ui-light/**` primitives OUTSIDE L3's
 * file ownership, hardcoding the OLD `LIGHT_TOKENS` palette (`#F6F5F2`
 * cool near-white) internally with no `surface="paper"` variant.
 *
 * RESOLVED at integration (2026-07-17, brief §2f): all four primitives
 * gained the additive `surface?: 'light' | 'paper'` prop (default
 * `'light'`, zero change for their other consumers — the dev gallery
 * specimens); `LightInterlude.tsx` now passes `surface="paper"` into
 * `LightBand`, and this file passes it into every `LightEyebrow`/`BigStat`/
 * `BigStatCurve` call below. The hue seam this note used to flag is closed.
 *
 * R8 restructure (2026-08-12, §St "Stats — 'What runs through CMG
 * Workflow.'", `.claude/handoffs/2026-08-12-r8-restructure-contract.md`):
 * restyle only, no data change (the recon build map confirms the snapshot
 * pipeline already serves exactly these three fields). The local
 * `PROOF_EYEBROW` chip copy and the `light.proof.{headlineLead,headlineRest,
 * statLabels}` chrome keys are SUPERSEDED by the new closed-manifest
 * `LANDING_CONTENT.statsBand` key (eyebrow/h2/sub/labels) added for this
 * round — `light.proof.{unavailable,curveCaption,curveSrSummaryTemplate}`
 * are UNCHANGED and still own the fail-closed line + the curve's real/
 * illustrative captions (statsBand has no equivalents for those). The H2
 * drops its two-tone lead/rest span split (statsBand.h2 is one string, not
 * a clause pair) in favor of the single-color serif treatment every other
 * R8 section header uses; a new mono sub-line ("Platform snapshot" — no
 * "live" claim, the aggregate is Redis-cached 1h)
 * renders between the H2 and the stat row. `labels.rate` replaces the old
 * generic "Conversion rate" wording with "Lead-to-client rate" (contract
 * §St) — same real `snapshot.conversionRate` field, renamed chrome only.
 * No amber emphasis added: the contract permits (never requires) amber on
 * one figure, and every other concurrent R8 lane is drawing from the same
 * page-wide ≤5%/viewport amber budget — skipping the optional accent here
 * is the smallest-footprint choice for a restyle-only pass.
 */

import { useCallback, useRef, useState } from 'react';
import type { SocialProofSnapshot } from '../lib/social-proof-snapshot';
import { LANDING_CONTENT } from '../constants/content';
import { useLocale } from '../hooks/useLocale';
import { useReducedMotion } from '../hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import { LightEyebrow } from '../ui-light/LightEyebrow';
import { BigStat } from '../ui-light/BigStat';
import { BigStatCurve } from '../ui-light/BigStatCurve';
import { PAPER_INK } from '../ui/paper-tokens';
import BigStatsMotion from './social-proof/BigStatsMotion';
import { formatMetricCount, formatMetricRate } from './social-proof/formatters';

/** Cross-lane contract (untouched, Law #7) — re-exported (type-only) from
 * the canonical producer `lib/social-proof-snapshot.ts`. */
export type { SocialProofSnapshot };

interface SocialProofSectionProps {
  snapshot: SocialProofSnapshot | null;
}

// [MAJOR fix, r4-p3-review-correctness.md]: `BigStatCurve`'s SVG
// `aria-label` was hardcoded Romanian regardless of page locale.
// `BigStatCurve` is an RSC primitive with no `useLocale()` of its own (same
// plain-string convention as every other `ui-light/**`/`ui/**` primitive —
// locale resolution happens HERE), so the resolved string is threaded in as
// a required prop. Section-local — this curve caption/label chrome is
// outside the `statsBand` closed manifest (which owns only eyebrow/h2/sub/
// labels), same precedent `light.proof.curveCaption` documents.
const CURVE_ARIA_LABEL = {
  en: 'Illustrative growth curve',
  ro: 'Curbă ilustrativă de creștere',
};

// R5-P3 [ADV-5/23]: the truthful counterpart of CURVE_ARIA_LABEL for when
// `monthlyLeads` is real — the SVG's own accessible name must never keep
// claiming "illustrative" once it is driven by real data. Section-local,
// same convention as every other const in this comment block.
// R5-P4 fix [ADV-23]: a function of the real series length, not a hardcoded
// "12" — the trailing window is now bounded to the platform's actual
// history (6-12 months, MIN_MONTHLY_WINDOW_MONTHS), same convention as
// KPI_SR_TEMPLATE/KPI_SR_UNAVAILABLE below (locale -> function of the
// runtime value).
const CURVE_ARIA_LABEL_REAL = {
  en: (months: number) => `Growth curve, last ${months} months`,
  ro: (months: number) => `Curbă de creștere, ultimele ${months} luni`,
};

const KPI_SR_TEMPLATE = {
  en: (label: string, value: string) => `${label}: ${value}, real value from snapshot.`,
  ro: (label: string, value: string) => `${label}: ${value}, valoare reală din snapshot.`,
};
const KPI_SR_UNAVAILABLE = {
  en: (label: string) => `${label}: unavailable.`,
  ro: (label: string) => `${label}: indisponibil.`,
};

const BIG_STAT_VALUE_UNAVAILABLE_CLASS =
  'text-[clamp(36px,3.6vw,52px)] font-[590] leading-none tracking-[-0.02em]';
// R6 mono voice law (judge §2.3): converted from font-mono to Inter --
// leading-none -> leading-4 and font-medium -> font-semibold to match the
// judge's "Inter text-[11px] leading-4 font-semibold uppercase" target; also
// used for the curve caption below (same const, same conversion).
const BIG_STAT_LABEL_CLASS = 'text-[11px] leading-4 font-semibold uppercase tracking-[0.06em]';

/** The `snapshot === null` twin of `BigStat` — same left-rule + number +
 * label grammar, but a static em dash and no ticker ([AMENDED per A6]:
 * "no ticker, no controller mounted"). `BigStat` itself has no such
 * variant, so this stays a small local composition rather than a fork of
 * the frozen `ui-light/**` primitive.
 *
 * R7 paper flip: every ink class this local component owns directly moved
 * off `ui-light/light-tokens.ts`'s `LIGHT_TOKENS` to `ui/paper-tokens.ts`'s
 * `PAPER_INK` (design contract L3.1 "All text to PAPER_INK.*"). The left
 * rule's FILL (not text) has no `bg` recipe in `PaperInkRecipe` today
 * (unlike `LIGHT_TOKENS.statRuleBg`, a dedicated fill token) — flagged for
 * integration as a registry gap; consumed here via inline `style` sourcing
 * `PAPER_INK.primary.hex` (the type's own documented escape hatch for a
 * consumer that "cannot pre-bake a static class") rather than typing a raw
 * `bg-[#hex]` literal or interpolating the registry hex into one (both
 * banned — the latter is invisible to Tailwind's static JIT extractor). */
function UnavailableStat({ label, srText }: { label: string; srText: string }) {
  return (
    <div className="flex items-stretch gap-3">
      <div aria-hidden="true" className="flex items-center">
        <span
          className="w-0.5 rounded-full"
          style={{ height: '60%', backgroundColor: PAPER_INK.primary.hex }}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <span
          aria-hidden="true"
          className={cn(BIG_STAT_VALUE_UNAVAILABLE_CLASS, PAPER_INK.primary.text)}
        >
          —
        </span>
        <span className={cn(BIG_STAT_LABEL_CLASS, PAPER_INK.muted.text)}>{label}</span>
        <span className="sr-only">{srText}</span>
      </div>
    </div>
  );
}

export default function SocialProofSection({ snapshot }: SocialProofSectionProps) {
  const { light, statsBand } = LANDING_CONTENT;
  const { t, locale } = useLocale();
  const prefersReducedMotion = useReducedMotion();
  const sectionRef = useRef<HTMLElement | null>(null);

  // R5-P3 post-review fix (SERIOUS finding, [ADV-18]): the count-up-together
  // orchestration's one remaining seam. `BigStatsMotion` fires `onStatsStart`
  // ONCE, from its own stats-row dwell trigger (>=0.5 visible + ~150ms
  // dwell — a stricter, separately-tuned bar than the block reveal); this
  // flips `statsStarted` true exactly once and threads it into all 3
  // `<BigStat startSignal>` below, so their `NumberTicker`s tick together
  // when the row is actually, comfortably in view — never each one
  // self-observing independently "at the band's first pixel" (the R4 kb
  // "serial timers finish before attention arrives" class). `useCallback`
  // keeps the reference stable across renders — `BigStatsMotion`'s effect
  // depends on it, and an unstable callback would tear down and rebuild
  // both its inView observers on every parent render.
  const [statsStarted, setStatsStarted] = useState(false);
  const handleStatsStart = useCallback(() => setStatsStarted(true), []);

  // R8: sourced from the new `statsBand.labels` manifest key — `.rate`
  // replaces the old `.conversion` wording ("Lead-to-client rate" vs
  // "Conversion rate", contract §St), same real `snapshot.conversionRate`
  // field underneath.
  const leadsLabel = t(statsBand.labels.leads);
  const clientsLabel = t(statsBand.labels.clients);
  const rateLabel = t(statsBand.labels.rate);

  // R5-P3 [ADV-5]: the real trailing-window series (R5-P4 fix: bounded to
  // the platform's actual history, 6-12 months — never assume exactly 12),
  // or null (privacy/validity gate failed, or no snapshot at all) — drives
  // BigStatCurve's real branch, the visible caption, and the sr summary
  // line below. Never re-derived per render beyond this cheap optional-
  // chain read.
  const monthlyLeads = snapshot?.monthlyLeads ?? null;

  return (
    <section
      ref={sectionRef}
      id="parteneri"
      // R6 rhythm (judge §2.9, three-beat interlude): Growth RESOLVES the
      // light interlude downward -- tight quote-bottom -> growth-top handoff
      // (small top pad), then opens generously into the section below (was
      // py-24 lg:py-32, the R5-P3 FAQ/Trust/FinalCTA-matched pair --
      // superseded this round by the interlude's own asymmetric beat).
      className="relative pt-12 pb-28 lg:pt-16 lg:pb-36 px-5 sm:px-6 lg:px-8 scroll-mt-24"
    >
      <div className="mx-auto max-w-[1100px]">
        <LightEyebrow surface="paper">{t(statsBand.eyebrow)}</LightEyebrow>
        {/* R5-P3 [ADV-13]: landing-serif-display owns family/weight 400/
            font-synthesis/tracking — font-semibold + the old tracking-tight
            class are DROPPED in this same edit. Ramp per amendment §1/§7:
            34px/1.15 mobile -> 40px/1.1 lg.
            R8 §St: single-color serif H2 (statsBand.h2 is one string, not a
            lead/rest clause pair) — matches every other R8 section header's
            single-tone treatment; the old two-span lead/secondary split is
            dropped in the same edit. */}
        <h2
          className={cn(
            'landing-serif-display mt-4 text-[34px] leading-[1.15] lg:text-[40px] lg:leading-[1.1]',
            PAPER_INK.primary.text
          )}
        >
          {t(statsBand.h2)}
        </h2>
        {/* R8 §St mono sub — statsBand.sub ("Platform snapshot"): the mono-voice
            uppercase caption grammar (Inter, not literal font-mono — R6 mono
            voice law), matching BIG_STAT_LABEL_CLASS below byte-for-byte so
            this line and the stat labels read as one register. */}
        <p className={cn('mt-2', BIG_STAT_LABEL_CLASS, PAPER_INK.muted.text)}>{t(statsBand.sub)}</p>

        {/* R5-P3 [ADV-18]: no-JS visitors never run BigStatsMotion's reveal
            effect at all — without this override, the SSR-hidden baseline
            below would strand them at opacity-0 forever (a regression vs.
            the pre-R5-P3 page, which had no JS-independent hiding). Scoped
            to this section's own two hook selectors only.
            R5-P4 fix (SERIOUS live-review finding — the fallback/
            illustrative branch never animated at all): this override, the
            hidden-baseline classes below, and the `<BigStatsMotion>` mount
            were all previously gated on `snapshot` truthiness — on the
            snapshot === null branch (what STAGING renders today), the row/
            curve rendered fully visible from first paint with NO reveal
            engine mounted at all. The reveal/sweep is disconnected from
            whether the KPI numbers are real (Law #7/#8 — `BigStatCurve`
            renders unconditionally either way); only the DIGIT count-up
            stays scoped to `snapshot` (via the untouched `{snapshot ? ... :
            <UnavailableStat/>}` ternary below — an em dash has nothing to
            tick up to). Un-gating this override to match. */}
        <noscript>
          <style>{`[data-big-stats-row],[data-big-stats-curve]{opacity:1!important;transform:none!important;clip-path:none!important}`}</style>
        </noscript>

        <div className="mt-10 grid grid-cols-1 items-center gap-8 md:mt-12 md:grid-cols-[minmax(0,34%)_1fr] md:gap-10">
          <div
            data-big-stats-row
            // [ADV-19] the whole row (label + digit, ticking or static) is
            // hidden from AT only in the real-snapshot branch — the sr-only
            // paragraph below already announces "label: value" as ONE
            // stable string per stat; without this, a screen reader would
            // hear the number twice. The unavailable branch's own per-stat
            // sr wiring (UnavailableStat) is untouched.
            aria-hidden={snapshot ? true : undefined}
            className={cn(
              'flex flex-col gap-6 sm:flex-row sm:gap-8 md:flex-col md:gap-7',
              // [ADV-18] SSR'd hidden baseline (never written by JS anymore
              // — see BigStatsMotion.tsx doc comment); motion-reduce is a
              // pure CSS counter, correct from paint one regardless of
              // JS/hydration timing. R5-P4 fix: UNCONDITIONAL now (was
              // `snapshot &&` gated) — the block reveal applies on BOTH
              // branches; only the digit count-up below stays scoped to
              // `snapshot` (an em dash has nothing to tick up to).
              'opacity-0 translate-y-3 motion-reduce:opacity-100 motion-reduce:translate-y-0'
            )}
          >
            {snapshot ? (
              <>
                <BigStat
                  value={snapshot.leadsProcessed}
                  label={leadsLabel}
                  locale={locale}
                  startSignal={statsStarted}
                  surface="paper"
                />
                <BigStat
                  value={snapshot.clientsWon}
                  label={clientsLabel}
                  locale={locale}
                  startSignal={statsStarted}
                  surface="paper"
                />
                <BigStat
                  value={snapshot.conversionRate}
                  decimalPlaces={1}
                  suffix="%"
                  label={rateLabel}
                  locale={locale}
                  startSignal={statsStarted}
                  surface="paper"
                />
              </>
            ) : (
              <>
                <UnavailableStat
                  label={leadsLabel}
                  srText={KPI_SR_UNAVAILABLE[locale](leadsLabel)}
                />
                <UnavailableStat
                  label={clientsLabel}
                  srText={KPI_SR_UNAVAILABLE[locale](clientsLabel)}
                />
                <UnavailableStat label={rateLabel} srText={KPI_SR_UNAVAILABLE[locale](rateLabel)} />
              </>
            )}
          </div>

          <div
            data-big-stats-curve
            className={cn(
              'relative',
              // [ADV-18] same SSR'd hidden baseline as the row, plus the
              // clip-path sweep's own starting mask (BigStatsMotion.tsx
              // animates BOTH the row's opacity/translate shape and this
              // clip-path in one WAAPI call). R5-P4 fix: UNCONDITIONAL now
              // (was `snapshot &&` gated) — `BigStatCurve` itself already
              // renders unconditionally in both the real and illustrative
              // states (file header doc comment), so its reveal/sweep must
              // too; the fallback branch was rendering fully visible from
              // first paint with no reveal engine mounted at all.
              'opacity-0 translate-y-3 [clip-path:inset(0_100%_0_0)] motion-reduce:opacity-100 motion-reduce:translate-y-0 motion-reduce:[clip-path:inset(0_0%_0_0)]'
            )}
          >
            <BigStatCurve
              label={
                monthlyLeads
                  ? CURVE_ARIA_LABEL_REAL[locale](monthlyLeads.length)
                  : t(CURVE_ARIA_LABEL)
              }
              series={monthlyLeads}
              surface="paper"
            />
            {/* [ADV-8] real data -> drop the illustrative label, but the
                curve still needs a caption; the surgeon's neutral key never
                claims growth the data must flatter (declining truth still
                renders this exact line). R5-P4 fix: `{count}` is the real
                (bounded, 6-12) series length, never a hardcoded "12". */}
            {monthlyLeads && (
              <p className={cn('mt-3', BIG_STAT_LABEL_CLASS, PAPER_INK.muted.text)}>
                {t(light.proof.curveCaption).replace('{count}', String(monthlyLeads.length))}
              </p>
            )}
          </div>
        </div>

        {snapshot ? (
          <p className="sr-only">
            {KPI_SR_TEMPLATE[locale](
              leadsLabel,
              formatMetricCount(snapshot.leadsProcessed, locale)
            )}{' '}
            {KPI_SR_TEMPLATE[locale](clientsLabel, formatMetricCount(snapshot.clientsWon, locale))}{' '}
            {KPI_SR_TEMPLATE[locale](rateLabel, formatMetricRate(snapshot.conversionRate, locale))}
          </p>
        ) : (
          <p className={cn('mt-8 text-sm', PAPER_INK.secondary.text)}>
            {t(light.proof.unavailable)}
          </p>
        )}

        {/* [ADV-19] the real curve's ONE stable sr-exposed value — the
            animated/static digits above stay visual-only; this is the
            single announced summary for the growth curve. */}
        {monthlyLeads && monthlyLeads.length > 0 && (
          <p className="sr-only">
            {t(light.proof.curveSrSummaryTemplate)
              .replace('{count}', String(monthlyLeads.length))
              .replace('{from}', formatMetricCount(monthlyLeads[0], locale))
              .replace('{to}', formatMetricCount(monthlyLeads[monthlyLeads.length - 1], locale))}
          </p>
        )}
      </div>

      {
        // R5-P3 [ADV-18] post-review fix (SERIOUS finding): the seam this
        // comment used to flag as open is now closed — `BigStat.tsx` forwards
        // `startSignal` to its inner `NumberTicker`, and `handleStatsStart`
        // above (a stable `useCallback`) flips `statsStarted` once this
        // engine's stats-row dwell trigger fires, threaded into all 3
        // `<BigStat>` above. The block reveal (opacity/rise/clip-path)
        // remains gated on the engine's OWN, lower `inViewAmount` bar —
        // only the digit count-up waits for the stricter dwell.
        //
        // R5-P4 fix (SERIOUS live-review finding — founder-reported +
        // lead-verified: the fallback/illustrative branch's curve/row never
        // animated at all): this mount is now UNCONDITIONAL (was `snapshot
        // &&` gated). `BigStatsMotion` never reads `snapshot` at all — it
        // only queries the `[data-big-stats-row]`/`[data-big-stats-curve]`
        // hooks, which render on BOTH branches, and its own PRM guard
        // (`prefersReducedMotion`) already handles reduced-motion safety
        // identically regardless of which branch mounted it. On the
        // fallback branch, `onStatsStart` firing is harmless (nothing reads
        // `statsStarted` in the `UnavailableStat` ternary below — an em dash
        // has nothing to tick up to); the count-up itself stays correctly
        // scoped to `snapshot` truthiness via that untouched ternary.
        <BigStatsMotion
          sectionRef={sectionRef}
          prefersReducedMotion={prefersReducedMotion}
          onStatsStart={handleStatsStart}
        />
      }
    </section>
  );
}
